import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [state, setState] = useState({ status: "verifying", email: "", reason: "" });
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const verify = useCallback(async () => {
    if (!token) { setState({ status: "invalid", reason: "invalid", email: "" }); return; }
    try {
      const { data } = await api.get(`/auth/password-reset/verify`, { params: { token } });
      if (data.valid) setState({ status: "valid", email: data.email || "", reason: "" });
      else setState({ status: "invalid", reason: data.reason || "invalid", email: "" });
    } catch (e) {
      setState({ status: "invalid", reason: "invalid", email: "" });
    }
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => nav("/auth"), 2000);
      return () => clearTimeout(t);
    }
  }, [done, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (p1.length < 8) { alert("Password must be at least 8 characters."); return; }
    if (p1 !== p2) { alert("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: p1 });
      setDone(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Reset failed — the link may have expired.");
    } finally { setSubmitting(false); }
  };

  const reasonCopy = {
    expired: "This reset link has expired. Request a new one.",
    used: "This reset link has already been used.",
    invalid: "This reset link is invalid.",
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-tb-navy-deep text-tb-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-tb-cream text-tb-navy rounded-2xl border-2 border-tb-gold shadow-2xl p-8">
        <Link to="/auth" className="inline-flex items-center gap-1 text-tb-navy/60 font-hand text-sm mb-4 hover:text-tb-navy">
          <ArrowLeft className="w-4 h-4" /> back to sign in
        </Link>
        {state.status === "verifying" && (
          <p data-testid="reset-password-verifying" className="font-hand text-tb-navy/60">checking link…</p>
        )}
        {state.status === "invalid" && (
          <div data-testid="reset-password-invalid" className="space-y-4">
            <h1 className="font-serif-it text-3xl">Link problem</h1>
            <p className="font-hand text-tb-navy/70">{reasonCopy[state.reason] || reasonCopy.invalid}</p>
            <Link
              data-testid="reset-password-request-new"
              to="/forgot-password"
              className="inline-block px-4 py-2 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand"
            >
              request a new link
            </Link>
          </div>
        )}
        {state.status === "valid" && !done && (
          <>
            <h1 className="font-serif-it text-3xl mb-1">Set a new password</h1>
            {state.email ? (
              <p className="font-hand text-tb-navy/60 text-sm mb-4">for {state.email}</p>
            ) : null}
            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block relative">
                <span className="block font-hand text-tb-navy/70 text-sm mb-1">New password</span>
                <input
                  data-testid="reset-password-p1"
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  required minLength={8}
                  value={p1}
                  onChange={(e) => setP1(e.target.value)}
                  className="w-full px-3 py-2 pr-10 rounded-lg bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
                />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-8 text-tb-navy/50" aria-label={show ? "Hide password" : "Show password"}>
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </label>
              <label className="block">
                <span className="block font-hand text-tb-navy/70 text-sm mb-1">Confirm new password</span>
                <input
                  data-testid="reset-password-p2"
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  required minLength={8}
                  value={p2}
                  onChange={(e) => setP2(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
                />
              </label>
              <button
                data-testid="reset-password-submit"
                type="submit"
                disabled={submitting}
                className="w-full py-2 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand disabled:opacity-50"
              >
                {submitting ? "updating…" : "reset password"}
              </button>
            </form>
          </>
        )}
        {done && (
          <div data-testid="reset-password-success" className="space-y-3">
            <div className="flex items-center gap-2 font-serif-it text-2xl text-tb-navy">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" /> Password updated
            </div>
            <p className="font-hand text-tb-navy/70">Redirecting to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
